import API_ENDPOINTS from "../config";

const SIGNAL_LABELS = {
  FINAL_SCORE: "Final Score",
  LIQUIDITY_SCORE: "Liquidity",
  MOMENTUM_SCORE: "Momentum",
  RECENT_DRAWDOWN: "Recent Drawdown",
  RELATIVE_STRENGTH: "Relative Strength",
  RETURN_20D: "20D Return",
  RVOL: "RVOL",
  TREND_SCORE: "Trend Score",
  TSI: "TSI Signal",
  VOLATILITY_SCORE: "Volatility",
  VOLUME_TREND: "Volume Trend",
};

const HIDDEN_RULES = new Set(["FINAL_SCORE", "TREND_SCORE", "RETURN_20D"]);

const UNCHECKED_BY_DEFAULT_SIGNAL_VALUES = {
  RVOL: new Set(["DEAD", "WEAK", "SPIKE"]),
  VOLATILITY_SCORE: new Set(["AVOID", "HIGH RISK", "RISKY"]),
  LIQUIDITY_SCORE: new Set(["DEAD LIQUIDITY", "LOW LIQUIDITY", "VERY LOW LIQUIDITY"]),
  RECENT_DRAWDOWN: new Set(["DAMAGED"]),
};

const CHECKED_ONLY_SIGNAL_VALUES = {
  MOMENTUM_SCORE: new Set(["STRONG CONTINUATION", "CONFIRMED"]),
};

const DEFAULT_CANDIDATE_PRICE_MIN = 100;
const DEFAULT_CANDIDATE_PRICE_MAX = 500;
const OPEN_ENDED_CANDIDATE_PRICE_MAX = 999999;

const SIGNAL_ORDER = [
  "RELATIVE_STRENGTH",
  "RVOL",
  "MOMENTUM_SCORE",
  "TSI",
  "VOLUME_TREND",
  "VOLATILITY_SCORE",
  "LIQUIDITY_SCORE",
  "RECENT_DRAWDOWN",
];

function toSignalId(ruleName) {
  return String(ruleName ?? "signal")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+(.)/g, (_, char) => char.toUpperCase())
    .replace(/[^a-zA-Z0-9]/g, "");
}

function formatSignalValue(value) {
  return String(value ?? "").replace(/_/g, " ");
}

function shouldCheckSignalValue(ruleName, value) {
  const normalizedValue = formatSignalValue(value).toUpperCase();
  const checkedOnly = CHECKED_ONLY_SIGNAL_VALUES[ruleName];
  const uncheckedValues = UNCHECKED_BY_DEFAULT_SIGNAL_VALUES[ruleName];

  if (checkedOnly) return checkedOnly.has(normalizedValue);
  if (uncheckedValues && uncheckedValues.has(normalizedValue)) return false;

  return true;
}

function parseApiResponse(data) {
  if (data?.ltResponseData?.responseData) {
    return JSON.parse(data.ltResponseData.responseData);
  }

  return data;
}

function normalizeStockDetail(data) {
  const parsed = parseApiResponse(data) ?? {};

  return {
    ...parsed,
    chartData: Array.isArray(parsed.chartData) ? parsed.chartData : [],
    histChartData: Array.isArray(parsed.histChartData) ? parsed.histChartData : [],
    stockSentiment: Array.isArray(parsed.stockSentiment) ? parsed.stockSentiment : [],
  };
}

function normalizeEvents(data) {
  const parsed = parseApiResponse(data);
  if (Array.isArray(parsed)) return parsed;
  if (Array.isArray(parsed?.rows)) return parsed.rows;
  if (Array.isArray(parsed?.events)) return parsed.events;
  return [];
}
function normalizeStrategyRuleConfig(data) {
  const parsed = parseApiResponse(data);
  if (Array.isArray(parsed)) return parsed;
  if (Array.isArray(parsed?.rows)) return parsed.rows;
  if (Array.isArray(parsed?.rules)) return parsed.rules;
  return [];
}

async function fetchJson(endpoint, options = {}) {
  const response = await fetch(endpoint, options);
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data?.message || `Request failed: ${response.status}`);
  }

  return data;
}

function normalizeSignalRules(rules) {
  const parsedRules = Array.isArray(rules)
    ? rules
    : Array.isArray(rules?.rows)
      ? rules.rows
      : [];

  const grouped = parsedRules.reduce((groups, rule) => {
    const ruleName = rule.ruleName ?? rule.rule_name ?? rule.name ?? "SIGNAL";
    const signal = rule.signal ?? rule.value ?? rule.signalValue ?? rule.signal_value;
    if (!signal || HIDDEN_RULES.has(ruleName)) return groups;

    if (!groups[ruleName]) {
      groups[ruleName] = {
        id: toSignalId(ruleName),
        ruleName,
        label: SIGNAL_LABELS[ruleName] ?? String(ruleName).replace(/_/g, " "),
        values: [],
        selectedValues: [],
      };
    }

    const displaySignal = formatSignalValue(signal);
    if (!groups[ruleName].values.includes(displaySignal)) {
      groups[ruleName].values.push(displaySignal);
      if (shouldCheckSignalValue(ruleName, signal)) {
        groups[ruleName].selectedValues.push(displaySignal);
      }
    }

    return groups;
  }, {});

  return Object.values(grouped).sort((a, b) => {
    const aIndex = SIGNAL_ORDER.indexOf(a.ruleName);
    const bIndex = SIGNAL_ORDER.indexOf(b.ruleName);
    if (aIndex === -1 && bIndex === -1) return a.label.localeCompare(b.label);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });
}

function formatDate(date) {
  return date.toISOString().slice(0, 10);
}

function buildDefaultPickRequest(strategy, signals, selectedFilters, pickSettings = {}) {
  const today = new Date();
  const dateFrom = new Date(today);
  dateFrom.setDate(today.getDate() - 90);
  const priceMin = pickSettings.priceMin === "" || pickSettings.priceMin === null || pickSettings.priceMin === undefined
    ? DEFAULT_CANDIDATE_PRICE_MIN
    : Number(pickSettings.priceMin);
  const priceMax = pickSettings.priceMax === "" && Number.isFinite(priceMin)
    ? OPEN_ENDED_CANDIDATE_PRICE_MAX
    : pickSettings.priceMax === null || pickSettings.priceMax === undefined
    ? DEFAULT_CANDIDATE_PRICE_MAX
    : Number(pickSettings.priceMax);

  const signalValues = signals.reduce((values, signalGroup) => {
    const selectedValues = selectedFilters[signalGroup.id] ?? [];
    if (signalGroup.ruleName && selectedValues.length) {
      values[signalGroup.ruleName] = selectedValues;
    }
    return values;
  }, {});

  return {
    dateFrom: formatDate(dateFrom),
    dateTo: formatDate(today),
    sectorId: "AL",
    industryId: "AL1",
    priceMin: Number.isFinite(priceMin) ? priceMin : null,
    priceMax: Number.isFinite(priceMax) ? priceMax : null,
    excludeNIZ: true,
    mode: String(strategy?.mode ?? "BALANCED").toUpperCase(),
    minScore: strategy?.minScore ?? 60,
    strategyCode: strategy?.code ?? strategy?.strategyCode ?? "",
    strategyLabel: strategy?.primaryStrategy ?? strategy?.label ?? "",
    signalValues,
  };
}

function flattenStockResponse(data) {
  if (Array.isArray(data?.stocks)) return data.stocks;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.listAnalysisDto)) return data.listAnalysisDto;
  if (!Array.isArray(data)) return [];

  const looksGrouped = data.some((item) => Array.isArray(item?.industries));
  if (!looksGrouped) return data;

  return data.flatMap((sectorGroup) =>
    (sectorGroup.industries ?? []).flatMap((industryGroup) =>
      (industryGroup.stocks ?? []).map((stock) => ({
        ...stock,
        sector: stock.sector ?? sectorGroup.sector,
        industry: stock.industry ?? industryGroup.industry,
      }))
    )
  );
}

function flattenTradeabilityDeskStocks(desk) {
  if (!Array.isArray(desk?.buckets)) return [];

  return desk.buckets.flatMap((bucket) =>
    (bucket.stocks ?? []).map((stock) => ({
      ...stock,
      tradeabilityBucket: stock.tradeabilityBucket ?? bucket.bucketCode,
      tradeabilityBucketLabel: bucket.bucketLabel,
    }))
  );
}

function firstDefined(...values) {
  return values.find((value) => value !== null && value !== undefined && value !== "");
}

function normalizeStock(stock, index, strategyName) {
  const symbol = stock.tickerId ?? stock.ticker_id ?? stock.stockId ?? stock.symbol ?? stock.ticker ?? `STOCK-${index + 1}`;
  const score = firstDefined(stock.score, stock.finalScore, stock.final_score, stock.rankScore);
  const confidenceScore = firstDefined(
    score,
    stock.confidenceScore,
    stock.confidence_score,
    stock.confidence
  );

  return {
    symbol,
    name: stock.companyName ?? stock.company_name ?? stock.name ?? stock.stockName ?? "-",
    sector: stock.sector ?? "-",
    industry: stock.industry ?? "-",
    strategy: stock.strategy ?? stock.tradeType ?? strategyName ?? "Strategy match",
    action: stock.action ?? stock.recommendedAction ?? stock.recommended_action ?? "-",
    confidenceScore: confidenceScore ?? "-",
    score: score ?? "-",
    matchedSignalCount: stock.matchedSignalCount ?? stock.matched_signal_count ?? 0,
    momentumCount: stock.momentumCount ?? stock.momentum_count ?? 0,
    trendCount: stock.trendCount ?? stock.trend_count ?? 0,
    liquidityCount: stock.liquidityCount ?? stock.liquidity_count ?? 0,
    volatilityCount: stock.volatilityCount ?? stock.volatility_count ?? 0,
    tsiCount: stock.tsiCount ?? stock.tsi_count ?? 0,
    relativeStrengthCount: stock.relativeStrengthCount ?? stock.relative_strength_count ?? 0,
    rvolCount: stock.rvolCount ?? stock.rvol_count ?? 0,
    drawdownCount: stock.drawdownCount ?? stock.drawdown_count ?? 0,
    volumeTrendCount: stock.volumeTrendCount ?? stock.volume_trend_count ?? 0,
    price: firstDefined(stock.liveLtp, stock.price, stock.closePrice, stock.close),
    quoteStatus: stock.quoteStatus,
    exchange: stock.exchange,
    tradingSymbol: stock.tradingSymbol,
    liveLtp: stock.liveLtp,
    liveOpen: stock.liveOpen,
    liveHigh: stock.liveHigh,
    liveLow: stock.liveLow,
    liveClose: stock.liveClose,
    livePercentChange: stock.livePercentChange,
    signals: stock.signals ?? {},
    reason: stock.whyPicked ?? stock.why_picked ?? stock.reason ?? stock.comments ?? "",
    whatToWatch: stock.whatToWatch ?? stock.what_to_watch ?? "",
    riskNote: stock.riskNote ?? stock.risk_note ?? "",
    tradeabilityStatus: stock.tradeabilityStatus,
    tradeabilityBucket: stock.tradeabilityBucket,
    tradeabilityBucketLabel: stock.tradeabilityBucketLabel,
    tradeabilityScore: stock.tradeabilityScore,
    liquidityLabel: stock.liquidityLabel,
    spreadLabel: stock.spreadLabel,
    volumeTrendLabel: stock.volumeTrendLabel,
    entryTriggerLabel: stock.entryTriggerLabel,
    lastCheckedAt: stock.lastCheckedAt,
    reasonCodes: Array.isArray(stock.reasonCodes) ? stock.reasonCodes : [],
    reasons: Array.isArray(stock.reasons) ? stock.reasons : [],
    checks: Array.isArray(stock.checks) ? stock.checks : [],
  };
}

export async function fetchSignalRules() {
  const response = await fetch(API_ENDPOINTS.SIGNAL_RULES);
  const data = await response.json();

  if (!response.ok) {
    throw new Error("Signal rules API failed");
  }

  return normalizeSignalRules(parseApiResponse(data));
}
export async function fetchStrategyRuleConfig(strategyCode) {
  if (!strategyCode || strategyCode === "CUSTOM") return [];
  const encodedStrategyCode = encodeURIComponent(strategyCode);
  const data = await fetchJson(`${API_ENDPOINTS.STRATEGY_RULE_CONFIG}/${encodedStrategyCode}`);
  return normalizeStrategyRuleConfig(data);
}

export async function fetchSignalLab() {
  const data = await fetchJson(API_ENDPOINTS.SIGNAL_LAB);
  const parsed = parseApiResponse(data);

  return {
    ...parsed,
    marketBiasSections: Array.isArray(parsed?.marketBiasSections) ? parsed.marketBiasSections : [],
    strategies: Array.isArray(parsed?.strategies) ? parsed.strategies : [],
    signalRules: normalizeStrategyRuleConfig(parsed?.signalRules),
  };
}

export async function previewStrategy(request) {
  const data = await fetchJson(`${API_ENDPOINTS.STRATEGY_ADMIN}/preview`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(request),
  });
  const parsed = parseApiResponse(data);
  return {
    ...parsed,
    candidates: (parsed?.candidates ?? []).map((stock, index) =>
      normalizeStock(stock, index, request?.sourceStrategyCode)
    ),
  };
}

export async function fetchStrategyDrafts() {
  const parsed = parseApiResponse(await fetchJson(`${API_ENDPOINTS.STRATEGY_ADMIN}/drafts`));
  return Array.isArray(parsed) ? parsed : [];
}

export async function fetchStrategyDraft(strategyCode) {
  return parseApiResponse(await fetchJson(`${API_ENDPOINTS.STRATEGY_ADMIN}/drafts/${encodeURIComponent(strategyCode)}`));
}

export async function createStrategyDraft(request) {
  return parseApiResponse(await fetchJson(`${API_ENDPOINTS.STRATEGY_ADMIN}/drafts`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(request),
  }));
}

export async function updateStrategyDraft(strategyCode, request) {
  return parseApiResponse(await fetchJson(`${API_ENDPOINTS.STRATEGY_ADMIN}/drafts/${encodeURIComponent(strategyCode)}`, {
    method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(request),
  }));
}

export async function activateStrategyDraft(strategyCode) {
  return parseApiResponse(await fetchJson(`${API_ENDPOINTS.STRATEGY_ADMIN}/drafts/${encodeURIComponent(strategyCode)}/activate?actor=portal-admin`, {
    method: "POST",
  }));
}

export async function simulateStrategyDraftTradeability(strategyCode, request) {
  return parseApiResponse(await fetchJson(
    `${API_ENDPOINTS.STRATEGY_ADMIN}/drafts/${encodeURIComponent(strategyCode)}/tradeability-simulation`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    }
  ));
}

export async function fetchStockPicks({ strategy, signals, selectedFilters, pickSettings }) {
  const request = buildDefaultPickRequest(strategy, signals, selectedFilters, pickSettings);
  const isCustomStrategy = String(request.strategyCode ?? strategy?.code ?? strategy?.strategyCode ?? "").toUpperCase() === "CUSTOM";
  const endpoint = isCustomStrategy ? API_ENDPOINTS.STOCK_PICK : API_ENDPOINTS.STRATEGY_STOCK_PICK;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(`${isCustomStrategy ? "Custom" : "Strategy"} stock pick API failed`);
  }

  return flattenStockResponse(parseApiResponse(data)).map((stock, index) =>
    normalizeStock(stock, index, strategy?.primaryStrategy)
  );
}

export async function fetchTradeabilityDesk({ strategy, signals, selectedFilters, pickSettings }) {
  const request = {
    ...buildDefaultPickRequest(strategy, signals, selectedFilters, pickSettings),
    monitorLimit: pickSettings?.monitorLimit ?? 50,
  };

  const response = await fetch(API_ENDPOINTS.TRADEABILITY_DESK, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error("Tradeability decision desk API failed");
  }

  const desk = parseApiResponse(data);
  const stocks = flattenTradeabilityDeskStocks(desk).map((stock, index) =>
    normalizeStock(stock, index, strategy?.primaryStrategy)
  );

  return {
    ...desk,
    stocks,
    buckets: Array.isArray(desk?.buckets) ? desk.buckets : [],
    summary: desk?.summary ?? {},
  };
}

export async function fetchSavedTradeabilityDesk({ strategy, signals, selectedFilters, pickSettings }) {
  const request = buildDefaultPickRequest(strategy, signals, selectedFilters, pickSettings);
  const strategyCode = request.strategyCode ?? strategy?.code ?? strategy?.strategyCode;
  if (!strategyCode || String(strategyCode).toUpperCase() === "CUSTOM") {
    return null;
  }

  const query = new URLSearchParams({
    marketDate: request.dateTo,
  });
  const data = await fetchJson(`${API_ENDPOINTS.TRADEABILITY_DESK}/${encodeURIComponent(strategyCode)}?${query.toString()}`);
  const desk = parseApiResponse(data);
  const stocks = flattenTradeabilityDeskStocks(desk).map((stock, index) =>
    normalizeStock(stock, index, strategy?.primaryStrategy)
  );

  return {
    ...desk,
    stocks,
    buckets: Array.isArray(desk?.buckets) ? desk.buckets : [],
    summary: desk?.summary ?? {},
  };
}

export async function refreshOneTradeabilityCandidate({ stock, strategy, signals, selectedFilters, pickSettings }) {
  const request = {
    ...buildDefaultPickRequest(strategy, signals, selectedFilters, pickSettings),
    symbol: stock?.symbol,
  };

  const response = await fetch(API_ENDPOINTS.NSE_TRADEABILITY_REFRESH_ONE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok || data?.success === false) {
    throw new Error(data?.message || "Single stock tradeability refresh failed");
  }

  const refreshedStock = parseApiResponse(data);
  if (!refreshedStock?.symbol || refreshedStock.symbol !== stock?.symbol) {
    throw new Error(refreshedStock?.message || refreshedStock?.status || "NSE refresh returned no stock quote");
  }

  return normalizeStock(refreshedStock, 0, strategy?.primaryStrategy);
}

export async function fetchStockDetail(stockId, dateFrom, dateTo) {
  const query = new URLSearchParams({
    stockId,
    dateFrom,
    dateTo,
  });
  const data = await fetchJson(`${API_ENDPOINTS.STOCK_DETAIL}?${query.toString()}`);
  return normalizeStockDetail(data);
}

export async function updateStockDecision(stockId, nizFlag, nizComments) {
  const query = new URLSearchParams({
    stockId,
    nizFlag: String(Boolean(nizFlag)),
    nizComments: nizComments ?? "",
  });

  const data = await fetchJson(`${API_ENDPOINTS.UPDATE_STOCK}?${query.toString()}`, {
    method: "POST",
  });

  return parseApiResponse(data);
}

async function fetchAdvisorDeskFromEndpoint(endpoint, options = {}) {
  const response = await fetch(endpoint, options);
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error("Advisor API failed");
  }

  const deskData = parseApiResponse(data);
  const signals = await fetchSignalRules();
  if (!signals.length) {
    throw new Error("Signal rules API returned no signal rules");
  }

  return {
    ...deskData,
    signals,
    signalSource: "API",
  };
}

export async function fetchAdvisorDesk() {
  return fetchAdvisorDeskFromEndpoint(API_ENDPOINTS.ADVISOR_DESK);
}

export async function refreshAdvisorDesk() {
  return fetchAdvisorDeskFromEndpoint(API_ENDPOINTS.ADVISOR_DESK_REFRESH, {
    method: "POST",
  });
}

export async function refreshMarketBiasInputs() {
  const data = await fetchJson(API_ENDPOINTS.MARKET_BIAS_REFRESH_INPUTS, {
    method: "POST",
  });

  return parseApiResponse(data);
}

export async function applyMarketBiasGlobalScore(request) {
  const data = await fetchJson(API_ENDPOINTS.MARKET_BIAS_APPLY_GLOBAL_SCORE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });

  return parseApiResponse(data);
}

export async function fetchLatestAppliedMarketBias() {
  const data = await fetchJson(API_ENDPOINTS.MARKET_BIAS_LATEST_APPLIED);
  return parseApiResponse(data);
}

export async function fetchEvents() {
  const data = await fetchJson(API_ENDPOINTS.EVENTS);
  return normalizeEvents(data);
}

export async function saveEvent(event) {
  const data = await fetchJson(API_ENDPOINTS.EVENTS, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(event),
  });

  return parseApiResponse(data);
}

export async function updateEvent(event) {
  const eventId = event?.eventId ?? event?.id;
  if (!eventId) return saveEvent(event);

  try {
    const data = await fetchJson(`${API_ENDPOINTS.EVENTS}/${eventId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(event),
    });

    return parseApiResponse(data);
  } catch (updateError) {
    console.warn("Event PUT endpoint unavailable; trying save endpoint.", updateError);
    return saveEvent(event);
  }
}

export async function updateEventEnabled(event, enabled) {
  const eventId = event?.eventId ?? event?.id;
  if (!eventId) return saveEvent({ ...event, enabled });

  const data = await fetchJson(`${API_ENDPOINTS.EVENTS}/${eventId}/enabled/${enabled}`, {
    method: "PUT",
  });

  return parseApiResponse(data);
}

export async function deleteEvent(event) {
  const eventId = event?.eventId ?? event?.id;
  const eventCode = event?.eventCode ?? event?.code;

  if (eventId) {
    try {
      return await fetchJson(`${API_ENDPOINTS.EVENTS}/${eventId}`, { method: "DELETE" });
    } catch (deleteError) {
      console.warn("Event DELETE endpoint unavailable; trying action endpoint.", deleteError);
    }
  }

  return fetchJson(`${API_ENDPOINTS.EVENTS}/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ eventId, eventCode }),
  });
}
